import type { ReactElement } from 'react';
import { useState } from 'react';

import { monthAndDay, monthDayAndYear } from 'utils/dateTimes';
import { runsWebview } from 'utils/device';
import { get } from 'utils/network';
import { WebViewMessage, postMessage } from 'utils/webview';
import { ApiRoute, FeatureFlag } from 'config';

import Plan from 'models/Plan';
import type { IUpcomingMenu } from 'models/UpcomingMenu';
import useUpcomingMenus from 'hooks/useUpcomingMenus';
import useNextDeliveryDate from 'hooks/useNextDeliveryDate';
import useIsAnyExtraSoldOut from 'hooks/useIsAnyExtraSoldOut';
import useIsAnyMenuCustomized from 'hooks/useIsAnyMenuCustomized';
import useAsyncEffect from 'hooks/useAsyncEffect';

import Modal from 'components/shared/Modal';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import useCurrentUser from '../../../hooks/useCurrentUser';

type Props = {
  plan: Plan;
  onClose: () => void;
};

function PlanConfirmationModal({ plan, onClose }: Props): ReactElement {
  const getNextDeliveryDate = useNextDeliveryDate();
  const isAnyExtraSoldOut = useIsAnyExtraSoldOut();
  const isAnyMenuCustomized = useIsAnyMenuCustomized();
  const { upcomingMenus: oldMenus, reload: reloadMenus } = useUpcomingMenus();
  const [nextDeliveryDate, setNextDeliveryDate] = useState<Date>();
  const [hasSoldOutItems, setHasSoldOutItems] = useState(false);
  const [hasCustomizedMenu, setHasCustomizedMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useAsyncEffect(async (isMounted) => {
    const response = await get<IUpcomingMenu[]>(ApiRoute.UPCOMING_MENUS);
    const newMenus = response.parsedBody;

    if (response.ok && isMounted.current) {
      // @ts-expect-error TODO: fix null safety
      setNextDeliveryDate(getNextDeliveryDate(newMenus));
      // @ts-expect-error TODO: fix null safety
      setHasSoldOutItems(isAnyExtraSoldOut(newMenus, plan));

      setHasCustomizedMenu(
        isAnyMenuCustomized(oldMenus)
        // @ts-expect-error TODO: fix null safety
        && oldMenus[1].plan_name === newMenus[1].plan_name,
      );

      setIsLoading(false);
      reloadMenus();
    }
  }, []);
  const goToMenusBtn = runsWebview ? (
    <button
      type="button"
      className="c-button c-button--primary"
      onClick={() => postMessage(WebViewMessage.NAVIGATE_TO_MENUS)}
    >
      Go To Menus
    </button>
  )
    : (
      <a
        className="c-button c-button--primary"
        href="/upcoming-menus"
      >
        Go To Menus
      </a>
    );

  return (
    <Modal
      show={!isLoading}
      onClose={onClose}
      className="c-modal-plan-select-confirmation"
      body={(
        <>
          <button
            className="close"
            type="button"
            aria-label="Close modal"
            onClick={onClose}
          >
            <CloseIcon width="20" height="20" />
          </button>

          <div
            className={[
              'c-modal-add-extras__title',
              'c-heading',
              'c-heading--brand-4',
            ].join(' ')}
          >
            You’re All Set!
          </div>

          <p className="u-text u-text--lg">
            {nextDeliveryDate && (
              <>
                {'Starting on '}
                <strong>
                  {monthDayAndYear(nextDeliveryDate)}
                </strong>
                &nbsp;
              </>
            )}

            {`${nextDeliveryDate ? 'y' : 'Y'}ou will receive:`}
            <br />
            <strong>{plan.description()}</strong>
            .
          </p>

          {hasSoldOutItems && !hasCustomizedMenu && (
            <>
              <br />
              <div
                className={[
                  'c-modal-add-extras__subtitle',
                  'c-heading',
                  'c-heading--brand-7',
                ].join(' ')}
              >
                {`NOTE ABOUT YOUR ${monthAndDay(nextDeliveryDate)} CART`}
              </div>

              <br />
              <div className="u-text u-text--xl">
                Unfortunately, the Extras you ordered are sold out. You don’t
                need to take any action—we’ve already removed the Extras from
                your cart—but you can visit Upcoming Menus to modify that week’s
                cart if you prefer.
              </div>
            </>
          )}

          {hasCustomizedMenu && (
            <div className="u-text u-text--xl">
              For your convenience, any carts you previously customized by
              swapping meals or adding Extras have not been changed.
            </div>
          )}

          <div className="u-text--center">
            {goToMenusBtn}
          </div>
        </>
      )}
    />
  );
}

export default PlanConfirmationModal;
